// src/pages/public/PortfolioPage.js
import { useState, useEffect } from "react";
import { useLang } from "../../context/LanguageContext";
import {
  getProjects, getExperiences, getSkills, getCertificates,
  getSettings, getServices, getTestimonials
} from "../../lib/firestore";
import Navbar from "../../components/shared/Navbar";
import Terminal from "../../components/public/Terminal";
import ContactForm from "../../components/public/ContactForm";
import ProjectCard from "../../components/public/ProjectCard";
import SEO from "../../components/shared/SEO";
import "../../styles/portfolio.css";

function useReveal() {
  useEffect(() => {
    const obs = new IntersectionObserver(
      entries => entries.forEach(e => { if (e.isIntersecting) e.target.classList.add("visible"); }),
      { threshold: 0.06 }
    );
    document.querySelectorAll(".reveal").forEach(el => obs.observe(el));
    return () => obs.disconnect();
  });
}

const SecHeader = ({ num, label }) => (
  <div className="sec-header reveal">
    <span className="sec-header__num">{num}</span>
    <h2 className="sec-header__title">{label}</h2>
    <div className="sec-header__line" />
  </div>
);

function useSectionNums(flags) {
  const nums = {};
  let n = 0;
  Object.entries(flags).forEach(([key, visible]) => {
    if (visible) nums[key] = String(++n).padStart(2, "0");
  });
  return nums;
}

export default function PortfolioPage() {
  const { t, getLang, lang } = useLang();
  const [data, setData] = useState({
    projects: [], experiences: [], skills: [],
    certificates: [], services: [], testimonials: []
  });
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  useReveal();

  useEffect(() => {
    // Google Analytics — sadece ana sayfada çalışır
    const script = document.createElement("script");
    script.src = "https://www.googletagmanager.com/gtag/js?id=G-L3HLL8JP9J";
    script.async = true;
    document.head.appendChild(script);

    script.onload = () => {
      window.dataLayer = window.dataLayer || [];
      function gtag() { window.dataLayer.push(arguments); }
      window.gtag = gtag;
      gtag("js", new Date());
      gtag("config", "G-L3HLL8JP9J");
    };
  }, []);

  useEffect(() => {
    Promise.all([
      getProjects(), getExperiences(), getSkills(), getCertificates(),
      getSettings(), getServices(), getTestimonials()
    ]).then(([projects, experiences, skills, certificates, s, services, testimonials]) => {
      setData({ projects, experiences, skills, certificates, services, testimonials });
      setSettings(s);
      setLoading(false);
    });
  }, []);

  const scroll = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  const g = (obj, field) => lang === "en"
    ? (obj?.[`${field}_en`] || obj?.[field] || "")
    : (obj?.[`${field}_tr`] || obj?.[field] || "");

  const name = settings?.name || "Dursun İnel";
  const desc = g(settings, "desc") || t.hero.desc;
  const email = settings?.email || "inl.dursun@gmail.com";
  const linkedin = settings?.linkedin || "";
  const github = settings?.github || "";
  const instagram = settings?.instagram || "";
  const available = settings?.available !== false;
  const availableText = lang === "en"
    ? (settings?.available_text_en || "Available for projects")
    : (settings?.available_text_tr || "Projelere açığım");
  const ctaTitle = g(settings, "cta_title") || (lang === "en" ? "Let's Build Your Project" : "Projenizi Hayata Geçirelim");
  const ctaDesc = g(settings, "cta_desc") || "";
  const processSteps = (lang === "en" ? settings?.process_steps_en : settings?.process_steps_tr) || [];
  const processDescs = (lang === "en" ? settings?.process_descs_en : settings?.process_descs_tr) || [];

  const nameParts = name.split(" ");
  const firstName = nameParts[0]?.toUpperCase() || "DURSUN";
  const lastName = nameParts.slice(1).join(" ")?.toUpperCase() || "İNEL";

  const show = {
    services: data.services.length > 0,
    skills: data.skills.length > 0,
    process: processSteps.length > 0,
    experience: data.experiences.length > 0,
    projects: data.projects.length > 0,
    certificates: data.certificates.length > 0,
    testimonials: data.testimonials.length > 0,
    contact: true,
    linkedin: !!linkedin,
    github: !!github,
    instagram: !!instagram,
  };

  // Sıra, sayfadaki JSX bölüm sırasıyla birebir aynı olmalı (numaralandırma buna göre üretilir)
  const nums = useSectionNums({
    services: show.services,
    skills: show.skills,
    experience: show.experience,
    projects: show.projects,
    certificates: show.certificates,
    testimonials: show.testimonials,
    contact: show.contact,
  });

  if (loading) return <div className="page-loading">Yükleniyor…</div>;

  return (
    <div>
      <SEO settings={settings} lang={lang} />
      <Navbar settings={settings} />

      {/* ─── HERO ─── */}
      <section className="hero-section">
        <div className="container" style={{ maxWidth: 1200 }}>
          <div className="row align-items-center g-5 hero-inner">
            <div className="col-lg-6 content-col">
              <div className={`hero-badge hero-badge--${available ? "available" : "unavailable"}`}>
                <span className={`hero-badge__dot hero-badge__dot--${available ? "available" : "unavailable"}`} />
                {availableText}
              </div>
              <h1 className="hero-name">
                {firstName}<br />
                <span className="hero-name__outline">{lastName}</span>
              </h1>
              <p className="hero-desc">{desc}</p>
              <div className="hero-cta">
                <button className="btn-primary" onClick={() => scroll("contact")}>{t.hero.cta2}</button>
                {show.projects && (
                  <button className="btn-outline" onClick={() => scroll("projects")}>{t.hero.cta1}</button>
                )}
              </div>
            </div>
            <div className="col-lg-6 terminal-col">
              <Terminal settings={settings} />
            </div>
          </div>
        </div>
      </section>

      {/* ─── STATS ─── */}
      <div className="container" style={{ maxWidth: 1200 }}>
        <div className="stats-grid reveal">
          {[
            [data.projects.length || "—", t.stats.projects],
            [settings?.years_exp || "—", t.stats.years],
            [data.certificates.length || "—", t.stats.certificates],
          ].map(([num, label], index) => (
            <div key={label} className="stat-box">
              <div className="stat-num">
                {index === 0 && num !== "—" ? `${num}+` : num}
              </div>
              <div className="stat-label">{label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ─── SERVICES ─── */}
      {show.services && (
        <section className="section" id="services">
          <div className="container" style={{ maxWidth: 1200 }}>
            <SecHeader num={nums.services} label={lang === "en" ? "Services" : "Hizmetler"} />
            <div className="services-grid reveal">
              {data.services.map((svc, i) => (
                <div key={svc.id || i} className="service-card">
                  <div className="service-icon">{svc.icon || "◈"}</div>
                  <div className="service-title">{g(svc, "title")}</div>
                  <p className="service-desc">{g(svc, "desc")}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ─── SKILLS ─── */}
      {show.skills && (
        <section className="section" id="skills">
          <div className="container" style={{ maxWidth: 1200 }}>
            <SecHeader num={nums.skills} label={t.sections.skills} />
            <div className="skills-grid reveal">
              {data.skills.map((skill, i) => (
                <div key={i} className="skill-item">
                  <div className="skill-dot" />
                  <span className="skill-name">{skill.name}</span>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ─── HOW I WORK ─── */}
      {show.process && (
        <section className="section section--dark" id="process">
          <div className="container" style={{ maxWidth: 1200 }}>
            <SecHeader num="→" label={lang === "en" ? "How I Work" : "Nasıl Çalışırım"} />
            <div className="process-grid reveal">
              {processSteps.map((step, i) => (
                <div key={i} className="process-step">
                  <div className="process-num">{String(i + 1).padStart(2, "0")}</div>
                  <div className="process-title">{step}</div>
                  <p className="process-desc">{processDescs[i] || ""}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ─── EXPERIENCE ─── */}
      {show.experience && (
        <section className="section" id="experience">
          <div className="container" style={{ maxWidth: 1200 }}>
            <SecHeader num={nums.experience} label={t.sections.experience} />
            <div className="exp-list">
              {data.experiences.map((exp, i) => (
                <div key={exp.id || i} className="exp-item reveal">
                  <div className="row">
                    <div className="col-md-3">
                      <div className="exp-date">{getLang(exp, "date")}</div>
                    </div>
                    <div className="col-md-9">
                      <div className="exp-role">{getLang(exp, "role")}</div>
                      <div className="exp-company">{exp.company}</div>
                      <p className="exp-desc">{getLang(exp, "desc")}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ─── PROJECTS ─── */}
      {show.projects && (
        <section className="section" id="projects">
          <div className="container" style={{ maxWidth: 1200 }}>
            <SecHeader num={nums.projects} label={t.sections.projects} />
            <div className="projects-grid reveal">
              {data.projects.map((proj, i) => (
                <ProjectCard key={proj.id || i} proj={proj} index={i} getLang={getLang} settings={settings} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ─── CERTIFICATES ─── */}
      {show.certificates && (
        <section className="section" id="certificates">
          <div className="container" style={{ maxWidth: 1200 }}>
            <SecHeader num={nums.certificates} label={t.sections.certificates} />
            <div className="certs-grid reveal">
              {data.certificates.map((cert, i) => {
                const hasUrl = !!cert.url;
                const inner = (
                  <>
                    <div className="cert-issuer">{cert.issuer}</div>
                    <div className="cert-name">
                      {getLang(cert, "name")}
                      {hasUrl && <span className="cert-arrow">↗</span>}
                    </div>
                  </>
                );
                // URL varsa gerçek <a> → klavye (Tab + Enter) ve "yeni sekmede aç" desteklenir
                return hasUrl ? (
                  <a
                    key={cert.id || i}
                    href={cert.url}
                    target="_blank"
                    rel="noreferrer"
                    className="cert-item cert-item--clickable"
                  >
                    {inner}
                  </a>
                ) : (
                  <div key={cert.id || i} className="cert-item cert-item--disabled">
                    {inner}
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* ─── TESTIMONIALS ─── */}
      {show.testimonials && (
        <section className="section section--dark" id="testimonials">
          <div className="container" style={{ maxWidth: 1200 }}>
            <SecHeader num={nums.testimonials} label={lang === "en" ? "What Clients Say" : "Müşteriler Ne Diyor"} />
            <div className={`row g-3 reveal`} style={{ margin: 0 }}>
              {data.testimonials.map((tm, i) => (
                <div key={tm.id || i} className={`col-md-${data.testimonials.length === 1 ? 12 : data.testimonials.length === 2 ? 6 : 4}`}>
                  <div className="testimonial-card">
                    <div className="testimonial-quote">"</div>
                    <p className="testimonial-text">{g(tm, "text")}</p>
                    <div className="testimonial-footer">
                      <div className="testimonial-name">{tm.name}</div>
                      <div className="testimonial-company">{g(tm, "company")}</div>
                      {tm.link && (
                        <a className="testimonial-link" href={tm.link} target="_blank" rel="noopener noreferrer">
                          {tm.link}
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ─── CONTACT ─── */}
      <section className="section" id="contact">
        <div className="container" style={{ maxWidth: 1200 }}>
          <div className="row g-5 centered-row">
            <div className="col-lg-5 reveal">
              <div style={{ position: "sticky", top: 120 }}>
                <div className="contact-num">{nums.contact}</div>
                <h2 className="contact-title">
                  {ctaTitle.split(" ").map((word, i, arr) =>
                    i === arr.length - 1
                      ? <span key={i} className="contact-title__accent">{word} </span>
                      : <span key={i}>{word} </span>
                  )}
                </h2>
                <p className="contact-desc">{ctaDesc}</p>

              </div>
            </div>
            <div className="col-lg-7 reveal">
              <div className="contact-form-box">
                <div className="contact-form-title">
                  {lang === "tr" ? "Proje Bildirimini Doldur" : "Fill Out the Project Brief"}
                </div>
                <ContactForm settings={settings} />
              </div>
            </div>
          </div>

          <br />

          <div className="contact-links">
            {[
              ["E-posta", email, `mailto:${email}`],
              show.linkedin && ["LinkedIn", "dursun-inel", linkedin],
              show.github && ["GitHub", "dursuninel", github],
              show.instagram && ["Instagram", "@dursuninel", instagram],
            ].filter(Boolean).map(([label, val, href]) => (
              <a key={label} href={href} target="_blank" rel="noreferrer" className="contact-link">
                <div>
                  <div className="contact-link__label">{label}</div>
                  <div className="contact-link__val">{val}</div>
                </div>
                <span className="contact-link__arrow">↗</span>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* ─── FOOTER ─── */}
      <footer className="site-footer">
        <div className="container footer-inner" style={{ maxWidth: 1200 }}>
          <span className="footer-text">© {new Date().getFullYear()} {name}</span>
          <span className="footer-text">{t.footer.rights}</span>
        </div>
      </footer>
    </div>
  );
}
